/**
 * Action handlers for the medical PA orders table toolbar and row interactions.
 * @module medicalPaOrdersTableHandlers
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import {
  refetchPAOrder,
  RefetchPAOrderPayload,
} from "../api/postCall/refetchPAOrder";
import { generateDataForDocViewerWithSideNavFromAuthOnFile } from "../components/modals/docViewerWithSideNavigation/utils/generateDataForDocViewerWithSideNavFromAuthOnFile";
import { createDataForWriteBackTrail } from "../components/modals/imageViewerWithBadgesModal/utils/createDataForWriteBackTrail";
import { ToastMessages } from "../components/toasts/toastMessages";
import { AuthStatusOptionModel } from "../data-model/authStatusOptions";
import { Hit } from "../data-model/postgresResponseForMedicalPaOrders";
import { ScreenNamesForBigQuery } from "../enums/eventsData";
import { EventsName } from "../enums/eventsName";
import { ModalId } from "../enums/modalId";
import { MedicalPaOrdersAlgoliaTableColumnKeys } from "../pages/worklists/medicalPaOrdersAlgolia/utils/medicalPaOrdersTable";
import { resetSingleMedicalPaOrder } from "../redux/slice/medicalPa/singleMedicalPaOrder";
import { setMetaData, setOpenedModalId } from "../redux/slice/modalSlice";
import { AppDispatch } from "../redux/store/store";
import { apiWorkerManager } from "../workerManagers/workerManager";
import { formatDateToUTCWithFormat } from "./formatDate";
import { isBlockedMedicalOrderStatus } from "./isInvalidMedicalOrderStatus";
import { refreshPostgresFacetCounts } from "./refreshPostgresFacetCounts";

/**
 * Navigates to the EV/BV detail page for a given order row, resetting the single order state first.
 * No-ops when the order is in development (empty status) or agent-in-progress.
 *
 * @param rowData - Table row data containing the order ID
 * @param dispatch - Redux dispatch function
 * @param navigate - React Router navigate function
 */
export const handleNavigateToNextPage = (
  rowData: any,
  dispatch: AppDispatch,
  navigate: (path: string) => void,
) => {
  const data = JSON.parse(rowData.rowData as string) as Hit;
  // Block navigation while the order is still being prepared or handled by an agent.
  if (isBlockedMedicalOrderStatus(data?.medical_order_status)) {
    return;
  }
  dispatch(resetSingleMedicalPaOrder());
  navigate(`/worklists/evbv/${rowData["id"]}`);
};

/**
 * Re-fetches PA order data for all currently selected (checked) rows in the table.
 * Calls the refetch API for each selected order in parallel and displays
 * success/partial/error toasts based on results.
 *
 * @param tableData - Current table row data with checkbox selection state
 * @param orgId - Organization/facility ID for the refetch payload
 * @param setIsRefetchingPAOrders - Loading state setter for the refetch operation
 */
export const handleRefetchPAOrderButtonClick = async (
  tableData: any[],
  orgId: string,
  setIsRefetchingPAOrders: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  const selectedOrders = tableData
    .filter(
      (row: any) =>
        row[MedicalPaOrdersAlgoliaTableColumnKeys.SELECT]?.isChecked,
    )
    .map((row: any) => ({
      id: row["id"],
      rowData: JSON.parse(row[MedicalPaOrdersAlgoliaTableColumnKeys.ROW_DATA]),
    }));

  if (selectedOrders.length === 0) {
    toast.error(ToastMessages.NO_ORDERS_SELECTED);
    return;
  }

  setIsRefetchingPAOrders(true);

  try {
    const promises = selectedOrders.map(async (order) => {
      const payload: RefetchPAOrderPayload = {
        service_type: order.rowData?.service_type,
        location: order.rowData?.location || "",
        order_id: order.id,
        practitioner_name: order.rowData?.practitioner_name || "",
        regimen_name: order.rowData?.regimen_name || "",
        date_of_service:
          formatDateToUTCWithFormat(
            order.rowData?.date_of_service,
            "MM/DD/YYYY",
          ) || "",
        org_id: order.rowData?.facility_id || orgId || "",
        patient_id: order.rowData?.patient_id || "",
      };
      try {
        await refetchPAOrder(payload);
        return { success: true, orderId: order.id };
      } catch {
        return { success: false, orderId: order.id };
      }
    });

    const results = await Promise.allSettled(promises);
    const successCount = results.filter(
      (r) => r.status === "fulfilled" && r.value.success,
    ).length;
    const failedCount = results.length - successCount;

    if (failedCount === 0) {
      toast.success(ToastMessages.REFETCH_PA_ORDER_SUCCESS);
    } else if (successCount === 0) {
      toast.error(ToastMessages.REFETCH_PA_ORDER_ERROR);
    } else {
      toast.success(ToastMessages.REFETCH_PA_ORDER_PARTIAL_SUCCESS);
    }
  } finally {
    setIsRefetchingPAOrders(false);
  }
};

/**
 * Opens the analytics date picker modal to initiate a data export flow.
 *
 * @param dispatch - Redux dispatch function
 */
export const exportDataForTable = (dispatch: AppDispatch) => {
  dispatch(setOpenedModalId({ id: ModalId.ANALYTICS_DATE_PICKER_MODAL }));
};

/**
 * Handles the table refresh action: re-fetches Postgres facet counts via
 * {@link refreshPostgresFacetCounts}, clears page-level selection, and resets
 * the initial data fetch flag to trigger a full table reload.
 *
 * @param dispatch - Redux dispatch function
 * @param setPagesForWhichSelectAllIsEnabled - Resets page-level select-all state
 * @param setInitialDataFetchingExecuted - Set to false to trigger a fresh data load
 */
export const handleRefreshButtonClick = async (
  dispatch: AppDispatch,
  setPagesForWhichSelectAllIsEnabled: React.Dispatch<
    React.SetStateAction<number[]>
  >,
  setInitialDataFetchingExecuted: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  // setMedicalPaOrdersState((prev) => ({ ...prev, loading: true }));
  // await new Promise((resolve) => setTimeout(resolve, 5000));
  // setMedicalPaOrdersState((prev) => ({ ...prev, loading: false }));
  // dispatch(
  //   fetchCountsByMasterAuthStatusByAlgolia({
  //     org_id: orgId || "",
  //     assigned_to: providerDocId,
  //   }),
  // );
  // dispatch(
  //   fetchMultiFacetsCountFromAlgolia({
  //     org_id: getOrgIdForFetchExternalWorklist(),
  //   }),
  // );
  refreshPostgresFacetCounts(dispatch);
  setPagesForWhichSelectAllIsEnabled([]);
  setInitialDataFetchingExecuted(false);
};

/**
 * Logs an analytics event when a user clicks on a PA order row in the worklist.
 *
 * @param rowData - Table row data containing order details and stringified JSON
 * @param orgId - Organization/facility ID
 * @param userId - Current user's ID
 * @param userEmail - Current user's email address
 */
export const logOrderClickedEvent = (
  rowData: any,
  orgId: string,
  userId: string,
  userEmail: string,
) => {
  const jsonData = JSON.parse(rowData.rowData);
  apiWorkerManager.enqueueAndProcess({
    patient_id: jsonData?.patient_id,
    org_id: orgId,
    order_id: rowData.id,
    event_name: EventsName.ORDER_CLICKED,
    user_id: userId,
    user_email: userEmail,
    additional_data: {
      screen_name: ScreenNamesForBigQuery.WORKLIST,
      payer_name: jsonData?.coverage?.primary?.payer_name ?? "",
      patient_name: `${jsonData?.first_name} ${jsonData?.last_name}`,
      drug_name: jsonData?.regimen_name ?? "",
      provider_name: jsonData?.practitioner_name ?? "",
      date_of_work: moment(jsonData?.created_at).format("YYYY-MM-DD"),
      date_of_service: jsonData?.date_of_service ?? "",
      patient_mrn: jsonData?.patient_id ?? "",
      source: ScreenNamesForBigQuery.ALL_PA_ORDERS,
    },
  });
};

/**
 * Opens the doc-viewer-with-side-navigation modal for a given order.
 * Fetches auth-on-file data from Postgres and populates the modal's items.
 * Aborts any in-flight fetch from a previous click before starting a new one.
 *
 * @param orderId - Order ID to fetch auth-on-file documents for
 * @param dispatch - Redux dispatch function
 * @param abortControllerRef - Mutable ref holding the active AbortController; updated on each call
 */
export const handleDocUploadScreenshotViewForExternalOrders = async (
  orderId: string,
  dispatch: AppDispatch,
  abortControllerRef: React.MutableRefObject<AbortController | null>,
) => {
  abortControllerRef.current?.abort();
  const controller = new AbortController();
  abortControllerRef.current = controller;
  dispatch(
    setOpenedModalId({
      id: ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION,
      metaData: { showLoader: true },
    }),
  );
  try {
    const data = await generateDataForDocViewerWithSideNavFromAuthOnFile(
      orderId,
      controller.signal,
    );
    if (controller.signal.aborted) return;
    dispatch(
      setMetaData({
        items: data,
        showLoader: false,
      }),
    );
  } catch (error) {
    if (!controller.signal.aborted) {
      dispatch(setMetaData({ items: [], showLoader: false }));
    }
  }
};

/**
 * Opens the write-back trail modal when the user clicks an RPA status cell.
 * Aborts any in-flight fetch from a previous click before starting a new one,
 * preventing stale data from overwriting a newer request's result.
 *
 * @param id - Order ID to fetch write-back trail data for
 * @param dispatch - Redux dispatch function
 * @param authStatusOptions - Auth status options needed to build trail data
 * @param abortControllerRef - Mutable ref holding the active AbortController; updated on each call
 */
export const handlePropagateEventOnClickInRpaStatusCell = async (
  id: string,
  dispatch: AppDispatch,
  authStatusOptions: AuthStatusOptionModel[] | undefined,
  abortControllerRef: React.MutableRefObject<AbortController | null>,
) => {
  abortControllerRef.current?.abort();
  const controller = new AbortController();
  abortControllerRef.current = controller;
  try {
    dispatch(
      setOpenedModalId({
        id: ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL,
        metaData: { showLoading: true },
      }),
    );
    const dataForWriteBackTrail = await createDataForWriteBackTrail(
      id,
      authStatusOptions ?? [],
      controller.signal,
    );
    if (controller.signal.aborted) return;
    dispatch(
      setMetaData({
        tabs: dataForWriteBackTrail.tabs,
        showLoading: false,
      }),
    );
  } catch {
    if (!controller.signal.aborted) {
      dispatch(setMetaData({ tabs: [], showLoading: false }));
    }
  }
};
